import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { LostReason } from '../constants';
import { LostReasonSelect } from './LostReasonSelect';
import type { LostReasonInput } from '../types';

interface LostReasonDialogProps {
  /** The deal being lost; the dialog is open while set. */
  dealName: string | null;
  onConfirm: (input: LostReasonInput) => void;
  /** Cancelling leaves the deal where it was. */
  onCancel: () => void;
}

/** Asks why a deal is lost before it moves to Lost (ADR-108 / FE-ADR-069). */
export function LostReasonDialog({ dealName, onConfirm, onCancel }: LostReasonDialogProps) {
  return (
    <Dialog open={dealName !== null} onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent>
        {/* Mounted only while open, so each opening starts blank. */}
        {dealName !== null ? (
          <LostReasonForm dealName={dealName} onConfirm={onConfirm} onCancel={onCancel} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function LostReasonForm({
  dealName,
  onConfirm,
  onCancel,
}: {
  dealName: string;
  onConfirm: (input: LostReasonInput) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<LostReason | ''>('');
  const [note, setNote] = useState('');

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason) onConfirm({ lost_reason: reason, lost_note: note.trim() });
      }}
    >
      <DialogHeader>
        <DialogTitle>Why was it lost?</DialogTitle>
        <DialogDescription>
          “{dealName}” moves to Lost. The reason feeds win/loss reporting.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-1.5">
        <Label htmlFor="lost-reason">Reason</Label>
        <LostReasonSelect
          id="lost-reason"
          aria-label="Lost reason"
          value={reason}
          onChange={setReason}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lost-note">Note (optional)</Label>
        <Textarea
          id="lost-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Who they chose, what the gap was…"
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="destructive" disabled={!reason}>
          Mark as lost
        </Button>
      </DialogFooter>
    </form>
  );
}
